(() => {
  if (window.__abrsmAnnouncement) {
    window.__abrsmAnnouncement.initAll();
    return;
  }

  const selector = '.abrsm-announcement';

  const collectRoots = (scope = document) => {
    const roots = [];
    if (scope.matches?.(selector)) roots.push(scope);
    scope.querySelectorAll?.(selector).forEach((root) => roots.push(root));
    return roots;
  };

  const initAnnouncement = (root) => {
    if (root.dataset.abrsmAnnouncementInitialized === 'true') return;
    root.dataset.abrsmAnnouncementInitialized = 'true';
    var controller = new AbortController();
    var signal = controller.signal;
    var tickerRaf = null;
    var timer = null;
    var resizeRaf = null;
    var countdownTimer = null;

    root.abrsmAnnouncementCleanup = function() {
      controller.abort();
      if (tickerRaf) window.cancelAnimationFrame(tickerRaf);
      if (resizeRaf) window.cancelAnimationFrame(resizeRaf);
      if (timer) window.clearInterval(timer);
      if (countdownTimer) window.clearInterval(countdownTimer);
      delete root.dataset.abrsmAnnouncementInitialized;
      delete root.abrsmAnnouncementCleanup;
    };

    var prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var visibleCount = parseInt(root.dataset.visibleCount || '0', 10);
    var mode = root.dataset.mode;
    var swipeThreshold = parseInt(root.dataset.swipeThreshold || '40', 10);

    function sanitizeClone(node) {
      node.removeAttribute('id');
      node.removeAttribute('data-shopify-editor-block');
      node.setAttribute('aria-hidden', 'true');
      Array.prototype.slice.call(node.querySelectorAll('[id]')).forEach(function(child) {
        child.removeAttribute('id');
      });
      return node;
    }

    // Dismiss persistence: localStorage for durable dismissal, sessionStorage for session-only dismissal.
    var dismissible = root.dataset.dismissible === 'true';
    var storageKey = 'abrsm_announcement_' + root.dataset.id;
    var days = parseInt(root.dataset.durable || '0', 10);
    if (dismissible) {
      try {
        var persisted = days > 0 ? localStorage.getItem(storageKey) : sessionStorage.getItem(storageKey);
        if (persisted) {
          if (days === 0 || Date.now() < parseInt(persisted, 10)) {
            root.hidden = true;
            return;
          }
          if (days > 0) localStorage.removeItem(storageKey);
        }
      } catch (error) {}
    }

    var dismissButton = root.querySelector('._dismiss');
    if (dismissButton) {
      dismissButton.addEventListener('click', function() {
        try {
          if (days > 0) {
            localStorage.setItem(storageKey, String(Date.now() + days * 86400000));
          } else {
            sessionStorage.setItem(storageKey, '1');
          }
        } catch (error) {}
        root.hidden = true;
      }, { signal: signal });
    }

    if (mode === 'ticker') {
      var tickerViewport = root.querySelector('._viewport');
      var tickerTrack = root.querySelector('._track');

      if (tickerViewport && tickerTrack && visibleCount > 1 && !prefersReducedMotion) {
        var originals = Array.prototype.slice.call(tickerTrack.children).filter(function(item) {
          return item.getAttribute('aria-hidden') !== 'true';
        });
        if (!tickerTrack.dataset.tickerCloned) {
          originals.forEach(function(item) {
            tickerTrack.appendChild(sanitizeClone(item.cloneNode(true)));
          });
          tickerTrack.dataset.tickerCloned = 'true';
        }

        function measureTicker() {
          var trackStyles = window.getComputedStyle(tickerTrack);
          var gap = parseFloat(trackStyles.columnGap || trackStyles.gap || '0') || 0;
          var width = originals.reduce(function(total, item) {
            return total + item.getBoundingClientRect().width;
          }, 0);
          width += Math.max(0, originals.length - 1) * gap;
          root.style.setProperty('--ticker-width', width + 'px');
          root.dataset.animate = width > tickerViewport.getBoundingClientRect().width ? 'true' : 'false';
        }

        tickerRaf = window.requestAnimationFrame(measureTicker);
        window.addEventListener('resize', measureTicker, { signal: signal });
      } else {
        root.dataset.animate = 'false';
      }
    }

    if (mode === 'carousel') {
      var carouselViewport = root.querySelector('._viewport');
      var carouselTrack = root.querySelector('._track');
      if (carouselTrack && carouselViewport) {
        if (!carouselTrack.firstElementChild || !carouselTrack.firstElementChild.classList.contains('_slide')) {
          var items = Array.prototype.slice.call(carouselTrack.children);
          items.forEach(function(item) {
            var slide = document.createElement('div');
            slide.className = '_slide';
            carouselTrack.insertBefore(slide, item);
            slide.appendChild(item);
          });
        }

        var slides = Array.prototype.slice.call(carouselTrack.children);
        var dotsWrap = root.querySelector('._dots');
        var controls = root.querySelector('._ctrls');
        var index = 0;
        var interval = parseInt(root.dataset.interval || '8000', 10);
        var autoplay = root.dataset.autoplay === 'true' && !prefersReducedMotion;
        timer = null;
        var touchStartX = 0;
        var slideWidth = 0;

        function stop() {
          if (timer) {
            window.clearInterval(timer);
            timer = null;
          }
        }

        function start() {
          if (!autoplay || slides.length < 2) return;
          stop();
          timer = window.setInterval(function() {
            goTo(index + 1);
          }, interval);
        }

        function renderDots() {
          if (!dotsWrap) return;
          dotsWrap.innerHTML = '';
          slides.forEach(function(_, slideIndex) {
            var dot = document.createElement('button');
            dot.type = 'button';
            dot.className = '_dot';
            dot.setAttribute('role', 'tab');
            dot.setAttribute('aria-label', 'Announcement ' + (slideIndex + 1));
            dot.setAttribute('aria-selected', 'false');
            dot.dataset.index = String(slideIndex);
            dot.addEventListener('click', function() {
              goTo(slideIndex);
            }, { signal: signal });
            dotsWrap.appendChild(dot);
          });
        }

        function sizeSlides() {
          slideWidth = Math.round(carouselViewport.getBoundingClientRect().width);
          if (!slideWidth) return;
          carouselTrack.style.width = String(slideWidth * slides.length) + 'px';
          slides.forEach(function(slide) {
            slide.style.width = String(slideWidth) + 'px';
            slide.style.minWidth = String(slideWidth) + 'px';
            slide.style.maxWidth = String(slideWidth) + 'px';
          });
        }

        function update() {
          sizeSlides();
          if (!slideWidth) return;
          carouselTrack.style.transform = 'translate3d(' + (-index * slideWidth) + 'px, 0, 0)';
          slides.forEach(function(slide, slideIndex) {
            slide.setAttribute('aria-hidden', slideIndex === index ? 'false' : 'true');
          });
          if (dotsWrap) {
            Array.prototype.slice.call(dotsWrap.children).forEach(function(dot, dotIndex) {
              dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false');
              dot.setAttribute('aria-selected', dotIndex === index ? 'true' : 'false');
            });
          }
        }

        function goTo(nextIndex) {
          index = (nextIndex + slides.length) % slides.length;
          update();
          start();
        }

        if (slides.length < 2) {
          if (controls) controls.hidden = true;
          update();
        } else {
          slides.forEach(function(slide, slideIndex) {
            slide.setAttribute('role', 'group');
            slide.setAttribute('aria-label', 'Announcement ' + (slideIndex + 1) + ' of ' + slides.length);
          });

          renderDots();
          update();
          start();

          Array.prototype.slice.call(root.querySelectorAll('._ctrl')).forEach(function(button) {
            button.addEventListener('click', function() {
              goTo(index + parseInt(button.dataset.dir || '0', 10));
            }, { signal: signal });
          });

          root.addEventListener('mouseenter', stop, { signal: signal });
          root.addEventListener('mouseleave', start, { signal: signal });
          root.addEventListener('focusin', stop, { signal: signal });
          root.addEventListener('focusout', function(event) {
            if (event.relatedTarget && root.contains(event.relatedTarget)) return;
            start();
          }, { signal: signal });
          root.addEventListener('keydown', function(event) {
            if (event.key === 'ArrowLeft') goTo(index - 1);
            if (event.key === 'ArrowRight') goTo(index + 1);
          }, { signal: signal });

          resizeRaf = null;
          window.addEventListener('resize', function() {
            if (resizeRaf) window.cancelAnimationFrame(resizeRaf);
            resizeRaf = window.requestAnimationFrame(update);
          }, { signal: signal });

          carouselTrack.addEventListener('touchstart', function(event) {
            touchStartX = event.changedTouches[0].clientX;
          }, { passive: true, signal: signal });

          carouselTrack.addEventListener('touchend', function(event) {
            var delta = event.changedTouches[0].clientX - touchStartX;
            if (Math.abs(delta) < swipeThreshold) return;
            goTo(index + (delta < 0 ? 1 : -1));
          }, { passive: true, signal: signal });
        }
      }
    }

    var countdowns = Array.prototype.slice.call(root.querySelectorAll('[data-countdown-until]'));
    if (countdowns.length) {
      function formatCountdown(totalSeconds, format) {
        var remaining = Math.max(0, totalSeconds);
        var daysPart = Math.floor(remaining / 86400);
        remaining -= daysPart * 86400;
        var hoursPart = Math.floor(remaining / 3600);
        remaining -= hoursPart * 3600;
        var minutesPart = Math.floor(remaining / 60);
        remaining -= minutesPart * 60;

        var output = [];
        if (format.indexOf('d') > -1) output.push(String(daysPart) + 'd');
        if (format.indexOf('h') > -1) output.push(String(hoursPart).padStart(2, '0') + 'h');
        if (format.indexOf('m') > -1) output.push(String(minutesPart).padStart(2, '0') + 'm');
        if (format.indexOf('s') > -1) output.push(String(remaining).padStart(2, '0') + 's');
        return output.join(' ');
      }

      function tick() {
        var now = Date.now();
        countdowns.forEach(function(item) {
          var target = parseInt(item.dataset.countdownUntil || '0', 10) * 1000;
          var display = item.querySelector('._cd');
          if (!display || !target) return;

          if (target <= now) {
            display.textContent = item.dataset.countdownExpired || 'Offer ended';
            item.classList.add('is-expired');
            return;
          }

          item.classList.remove('is-expired');
          var totalSeconds = Math.floor((target - now) / 1000);
          display.textContent = formatCountdown(totalSeconds, item.dataset.countdownFormat || display.dataset.format || 'dhms');
        });
      }

      tick();
      countdownTimer = window.setInterval(tick, 1000);
    }
  };

  const initAll = (scope = document) => collectRoots(scope).forEach(initAnnouncement);
  const destroyWithin = (scope) => collectRoots(scope).forEach((root) => root.abrsmAnnouncementCleanup?.());
  window.__abrsmAnnouncement = { initAll, destroyWithin };
  initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
})();
